import React from 'react';
import { Link } from 'react-router-dom';
import { ScrollAnimate } from '../components/ScrollAnimate';
import { SEO } from '../components/SEO';

const headingMain =
  'text-[32px] font-bold leading-[1.15] tracking-[-1px] text-[#0064E0] md:text-[48px]';
const headingSection = 'mt-10 text-[22px] font-semibold leading-tight text-[#0064E0]';
const linkClass = 'font-semibold text-[#0064E0] hover:underline';

export function TermsPage() {
  return (
    <>
      <SEO
        title="Terms of Service — logNsec"
        description="The terms governing your use of logNsec and ShieldSME."
        canonical="/terms"
      />
      <section className="bg-white px-5 py-20 md:px-8">
      <div className="mx-auto max-w-3xl">
        <ScrollAnimate>
          <h1 className={headingMain}>Terms of Service</h1>
          <p className="mt-3 text-label-site text-[#65676B]">Last updated: July 2025</p>

          <p className="mt-8 text-body-site">
            These Terms of Service (&quot;Terms&quot;) govern your access to and use of websites, products, and
            services offered by <strong className="font-semibold text-[#1C1E21]">logNsec</strong> (&quot;logNsec,&quot;
            &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), including{' '}
            <a href="https://lognsec.com" className={linkClass}>
              lognsec.com
            </a>{' '}
            and{' '}
            <a
              href="https://shieldsme.lognsec.com"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              ShieldSME
            </a>{' '}
            at shieldsme.lognsec.com. By accessing or using our services, you agree to these Terms. If you are
            entering into these Terms on behalf of a company or other legal entity, you represent that you have
            authority to bind that entity.
          </p>

          <h2 className={headingSection}>1. Acceptance of terms</h2>
          <p className="mt-4 text-body-site">
            By using our website, creating an account, purchasing a subscription, or otherwise using our services,
            you agree to be bound by these Terms and our{' '}
            <Link to="/privacy" className={linkClass}>
              Privacy Policy
            </Link>
            . If you do not agree, you must not use our services. We may refuse service or close accounts at our
            discretion where permitted by law.
          </p>

          <h2 className={headingSection}>2. Description of services</h2>
          <p className="mt-4 text-body-site">
            logNsec provides cybersecurity-related offerings, which may include, depending on what you use:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>
              <strong className="font-semibold text-[#1C1E21]">Corporate website</strong> (
              <a href="https://lognsec.com" className={linkClass}>
                lognsec.com
              </a>
              ) — information about logNsec, contact forms, and related content.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">ShieldSME</strong> — a software platform available at{' '}
              <a
                href="https://shieldsme.lognsec.com"
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                shieldsme.lognsec.com
              </a>{' '}
              that provides security monitoring, reporting, and related features as described in-product or in
              separate documentation.
            </li>
            <li>
              <strong className="font-semibold text-[#1C1E21]">Managed security services</strong> — professional
              services (such as managed detection and response or related engagements) that may be governed by a
              separate statement of work, order form, or master services agreement. Where those documents conflict
              with these Terms on a specific engagement, the engagement-specific agreement controls for that
              engagement.
            </li>
          </ul>
          <p className="mt-4 text-body-site">
            Features, availability, and pricing may change over time. We do not guarantee uninterrupted or
            error-free operation.
          </p>

          <h2 className={headingSection}>3. User accounts and responsibilities</h2>
          <p className="mt-4 text-body-site">
            To use certain services, you may need to register an account. You agree to provide accurate, current
            information and to keep it updated. You are responsible for:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>Maintaining the confidentiality of your login credentials and any API keys or tokens we issue.</li>
            <li>All activity that occurs under your account, except where caused by our gross negligence or breach
              of these Terms.</li>
            <li>Ensuring that your use complies with applicable laws and that you have any consents or rights
              needed to submit data (including customer or employee data) to our services.</li>
          </ul>
          <p className="mt-4 text-body-site">
            You must notify us promptly at{' '}
            <a href="mailto:hello@lognsec.com" className={linkClass}>
              hello@lognsec.com
            </a>{' '}
            if you suspect unauthorized access to your account.
          </p>

          <h2 className={headingSection}>4. Acceptable use policy</h2>
          <p className="mt-4 text-body-site">You agree not to:</p>
          <ul className="mt-3 list-disc space-y-2 pl-6 text-body-site">
            <li>Use our services for any unlawful purpose or in violation of any applicable law or regulation.</li>
            <li>Attempt to probe, scan, or test the vulnerability of our systems, or breach or circumvent any
              security or authentication measure, without our prior written consent.</li>
            <li>Reverse engineer, decompile, disassemble, or otherwise attempt to derive source code or underlying
              ideas from our software or services, except where such restrictions are prohibited by applicable law.</li>
            <li>Share, sell, or transfer your account credentials or access to third parties, or allow unauthorized
              persons to use your account.</li>
            <li>Use our services to distribute malware, conduct attacks against third parties, or harass, abuse, or
              harm others.</li>
            <li>Resell or redistribute our services in a way that misrepresents logNsec or violates our branding or
              technical policies.</li>
          </ul>
          <p className="mt-4 text-body-site">
            We may investigate violations and cooperate with law enforcement. We may suspend or terminate access for
            conduct we reasonably believe violates this section or creates risk to us or others.
          </p>

          <h2 className={headingSection}>5. Payment and billing</h2>
          <p className="mt-4 text-body-site">
            Paid subscriptions and purchases for ShieldSME (and other paid offerings we may offer) are processed
            through <strong className="font-semibold text-[#1C1E21]">Paystack</strong> or other payment processors we
            designate. By providing payment information, you authorize us and our payment partners to charge the
            applicable fees according to the plan, currency, and billing cycle you select.
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Regional pricing.</strong> Prices may vary by region or
            currency as shown at checkout or in-product. Taxes, if any, are your responsibility unless stated
            otherwise.
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Subscriptions.</strong> Unless otherwise stated,
            subscriptions renew automatically until you cancel in accordance with the cancellation method we provide.
            You remain responsible for fees incurred until cancellation takes effect.
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Refunds.</strong> Fees are generally non-refundable for
            any subscription period or portion of a period during which you had access to paid features, except
            where required by law or as we expressly agree in writing. If you believe a charge is in error, contact
            us at{' '}
            <a href="mailto:hello@lognsec.com" className={linkClass}>
              hello@lognsec.com
            </a>{' '}
            promptly.
          </p>

          <h2 className={headingSection}>6. Intellectual property</h2>
          <p className="mt-4 text-body-site">
            All rights, title, and interest in and to the logNsec website, ShieldSME, our branding, logos, software,
            documentation, and all related intellectual property (collectively, our &quot;IP&quot;) are owned by
            logNsec or our licensors. These Terms do not grant you any ownership rights in our IP.
          </p>
          <p className="mt-4 text-body-site">
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable,
            revocable license to access and use the services solely for your internal business purposes (or personal
            use where applicable), in accordance with any plan or documentation we provide. You may not copy,
            modify, distribute, publicly display, or create derivative works of our IP except as expressly
            permitted.
          </p>
          <p className="mt-4 text-body-site">
            You retain ownership of data you submit to the services. You grant us a license to host, process, and use
            that data as needed to provide and improve the services, consistent with our Privacy Policy.
          </p>

          <h2 className={headingSection}>7. Disclaimer of warranties</h2>
          <p className="mt-4 text-body-site">
            OUR SERVICES, INCLUDING ANY SECURITY TOOLS, REPORTS, OR RECOMMENDATIONS, ARE PROVIDED &quot;AS IS&quot;
            AND &quot;AS AVAILABLE.&quot; TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE DISCLAIM ALL WARRANTIES, WHETHER
            EXPRESS, IMPLIED, OR STATUTORY, INCLUDING IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR
            PURPOSE, TITLE, AND NON-INFRINGEMENT.
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">No guarantee of security outcomes.</strong> Cybersecurity
            tools and managed services are intended to help identify risks and reduce the likelihood or impact of
            incidents. They do <strong className="font-semibold text-[#1C1E21]">not</strong> guarantee that you will
            experience zero breaches, zero downtime, or complete protection against all threats. You remain
            responsible for your own security posture, backups, and compliance obligations.
          </p>

          <h2 className={headingSection}>8. Limitation of liability</h2>
          <p className="mt-4 text-body-site">
            TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL LOGNSEC, ITS AFFILIATES, DIRECTORS,
            OFFICERS, EMPLOYEES, OR SUPPLIERS BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,
            EXEMPLARY, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, GOODWILL, OR BUSINESS OPPORTUNITY, ARISING
            OUT OF OR RELATED TO THESE TERMS OR YOUR USE OF THE SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH
            DAMAGES.
          </p>
          <p className="mt-4 text-body-site">
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED
            TO THESE TERMS OR THE SERVICES IN ANY TWELVE (12) MONTH PERIOD SHALL NOT EXCEED THE GREATER OF (A) THE
            AMOUNTS YOU PAID TO LOGNSEC FOR THE SERVICES GIVING RISE TO THE CLAIM DURING THAT PERIOD, OR (B) ONE
            HUNDRED U.S. DOLLARS (USD $100), IF YOU PAID NO FEES.
          </p>
          <p className="mt-4 text-body-site">
            Some jurisdictions do not allow certain limitations; in those cases, our liability is limited to the
            fullest extent permitted by law.
          </p>

          <h2 className={headingSection}>9. Termination</h2>
          <p className="mt-4 text-body-site">
            You may stop using our services at any time. We may suspend or terminate your access if you materially
            breach these Terms, if we are required to do so by law, or if we discontinue a service (with reasonable
            notice where practicable).
          </p>
          <p className="mt-4 text-body-site">
            Upon termination, your right to use the services ceases. Provisions that by their nature should survive
            (including intellectual property, disclaimers, limitation of liability, governing law, and dispute
            resolution) will survive termination.
          </p>

          <h2 className={headingSection}>10. Governing law and dispute resolution</h2>
          <p className="mt-4 text-body-site">
            logNsec is a <strong className="font-semibold text-[#1C1E21]">United States</strong>-based company. Our
            services are delivered globally, and clients may be located anywhere in the world. These Terms and any
            dispute or claim arising out of or in connection with them or their subject matter (including
            non-contractual disputes) shall be governed by and construed in accordance with the laws of the{' '}
            <strong className="font-semibold text-[#1C1E21]">State of Wyoming, United States</strong>, without
            regard to its conflict-of-law principles.
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Informal resolution.</strong> Before initiating any
            formal dispute proceeding, the parties agree to attempt to resolve any dispute arising out of or
            relating to these Terms or the services through good-faith negotiation by contacting the other party
            at{' '}
            <a href="mailto:hello@lognsec.com" className={linkClass}>
              hello@lognsec.com
            </a>{' '}
            (or the contact details you provide to us).
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Binding arbitration (AAA).</strong> If a dispute is not
            resolved through good-faith negotiation within a reasonable period, either party may submit the dispute
            to binding arbitration administered by the{' '}
            <strong className="font-semibold text-[#1C1E21]">American Arbitration Association (AAA)</strong> in
            accordance with the AAA&apos;s applicable rules in effect at the time of the demand for arbitration.
            Judgment on the award rendered by the arbitrator may be entered in any court having jurisdiction.
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">International clients.</strong> If you are an
            international client (for example, you are organized, domiciled, or principally using the services from
            outside the United States), disputes shall be resolved under the{' '}
            <strong className="font-semibold text-[#1C1E21]">AAA International Arbitration Rules</strong> (or
            successor international rules published by the AAA or its international division), as applicable, instead
            of domestic AAA rules where those international rules govern cross-border matters. The parties may agree
            on the seat and language of the arbitration; if they do not, the AAA or the arbitrator may designate an
            appropriate seat and English as the language of the proceedings, subject to applicable mandatory law.
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Mandatory local rights.</strong> Nothing in these Terms
            limits any rights you may have under mandatory consumer protection or other laws of your country or
            region of residence that cannot be waived or limited by contract.
          </p>
          <p className="mt-4 text-body-site">
            Notwithstanding the foregoing, either party may seek interim or injunctive relief in a court of competent
            jurisdiction where necessary to protect intellectual property or confidential information, without
            waiving the agreement to arbitrate the underlying merits.
          </p>

          <h2 className={headingSection}>11. Changes to terms</h2>
          <p className="mt-4 text-body-site">
            We may modify these Terms from time to time. We will post the updated Terms on this page and update the
            &quot;Last updated&quot; date. For material changes to paid services, we may also provide notice by email
            or in-product notice where appropriate. Your continued use of the services after the effective date of
            changes constitutes acceptance of the revised Terms, except where applicable law requires a different
            process. If you do not agree, you must stop using the services.
          </p>

          <h2 className={headingSection}>12. Contact us</h2>
          <p className="mt-4 text-body-site">
            For questions about these Terms, contact logNsec at:
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Email:</strong>{' '}
            <a href="mailto:hello@lognsec.com" className={linkClass}>
              hello@lognsec.com
            </a>
          </p>
          <p className="mt-4 text-body-site">
            <strong className="font-semibold text-[#1C1E21]">Website:</strong>{' '}
            <a href="https://lognsec.com" className={linkClass}>
              lognsec.com
            </a>
          </p>
        </ScrollAnimate>
      </div>
    </section>
    </>
  );
}
